import lodashSet from 'lodash.set';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/redux/store';
import { questionnairesApi } from 'app/services/questionnaires';

export type TForeignEmployer = TForeignEmployerRow[];

export type TForeignEmployerRow = {
  foreignIeNonUSEmployerName: Nullable<string>;
  isEmployeraForeignCompanyaForeignSubsidiaryOfAUSCompanyOrAUSCompany: Nullable<string>;
  employersStreetAddress: Nullable<string>;
  City: Nullable<string>;
  PostCode: Nullable<string>;
  Country: Nullable<string>;
  countryGrossWagessalaryEarnedWithThisEmployerDuringYear: Nullable<string>;
  didYourEmployerProvideAnAllowanceOnTopOfDirectSalary: Nullable<TYesOrNo>;
  didYourEmployerProvideAnAllowanceOnTopOfDirectSalaryList: TDidYourEmployerProvideAnAllowanceOnTopOfDirectSalaryList;
  wereContributionsMadeToaNonUSPensionPlan: Nullable<TYesOrNo>;
  amountYourEmployerContributed: Nullable<string>;
};

export type TDidYourEmployerProvideAnAllowanceOnTopOfDirectSalaryList =
  TDidYourEmployerProvideAnAllowanceOnTopOfDirectSalaryListRow[];

export type TDidYourEmployerProvideAnAllowanceOnTopOfDirectSalaryListRow = {
  descriptionOfAllowance: Nullable<string>;
  amount: Nullable<string>;
};

export interface WagesStateValues {
  foreignEmployer: TForeignEmployer;
  incomeOutsideOfTheUs: Nullable<TYesOrNo>;
  didYouEarnWagesFromANonUsEmployer: Nullable<TYesOrNo>;
  didYouEarnWagesFromAUsEmployer: Nullable<TYesOrNo>;
  pleaseUploadFormW2: Nullable<string>;
  uploadAnnualNonUsWageStatementFromForeignEmployers: Nullable<string>;
  didYouReceiveAForeignieNonUsTaxRefund: Nullable<string>;
  amountOfForeignieNonUsTaxRefund: Nullable<string>;
  currency: Nullable<string>;
  didYouReceiveStockBasedCompensationFromYourEmployer: Nullable<string>;
  pleaseUploadSupportingDocumentation: Nullable<string>;
  incomeFromTheUs: Nullable<TYesOrNo>;
  didYouReceiveWagesDuringTheYearFromAUsEmployer: Nullable<TYesOrNo>;
  areYouAUsGovernmentEmployee: Nullable<string>;
  didYouPerformWorkAsAConsultantOrIndependentContractorForAUsFirm: Nullable<string>;
  pleaseUploadForm1099Misc: Nullable<string>;
  didYouReceiveRoyaltiesUnemploymentPaymentsOrOtherMiscellaneousIncomeFromAUsSource: Nullable<string>;
  pleaseUploadACopyOfForm1099MiscOrFormK1: Nullable<string>;
  wereContributionsMadeToaNonUSPensionPlan: Nullable<TYesOrNo>;
  amountYourEmployerContributed: Nullable<string>;
}

export interface WagesState {
  values: WagesStateValues;
}

const initialState: WagesState = {
  values: {
    incomeOutsideOfTheUs: null,
    didYouEarnWagesFromANonUsEmployer: null,
    didYouEarnWagesFromAUsEmployer: null,
    pleaseUploadFormW2: null,
    uploadAnnualNonUsWageStatementFromForeignEmployers: null,
    didYouReceiveAForeignieNonUsTaxRefund: null,
    amountOfForeignieNonUsTaxRefund: null,
    currency: null,
    didYouReceiveStockBasedCompensationFromYourEmployer: null,
    pleaseUploadSupportingDocumentation: null,
    incomeFromTheUs: null,
    didYouReceiveWagesDuringTheYearFromAUsEmployer: null,
    areYouAUsGovernmentEmployee: null,
    didYouPerformWorkAsAConsultantOrIndependentContractorForAUsFirm: null,
    pleaseUploadForm1099Misc: null,
    didYouReceiveRoyaltiesUnemploymentPaymentsOrOtherMiscellaneousIncomeFromAUsSource:
      null,
    pleaseUploadACopyOfForm1099MiscOrFormK1: null,
    foreignEmployer: [
      {
        foreignIeNonUSEmployerName: null,
        isEmployeraForeignCompanyaForeignSubsidiaryOfAUSCompanyOrAUSCompany:
          null,
        employersStreetAddress: null,
        City: null,
        PostCode: null,
        Country: null,
        countryGrossWagessalaryEarnedWithThisEmployerDuringYear: null,
        didYourEmployerProvideAnAllowanceOnTopOfDirectSalary: null,
        didYourEmployerProvideAnAllowanceOnTopOfDirectSalaryList: [
          { descriptionOfAllowance: null, amount: null },
        ],
        wereContributionsMadeToaNonUSPensionPlan: null,
        amountYourEmployerContributed: null,
      },
    ],
    wereContributionsMadeToaNonUSPensionPlan: null,
    amountYourEmployerContributed: null,
  },
};

const wagesSlice = createSlice({
  name: 'spouse/wages',
  initialState,
  reducers: {
    update(state, action: PayloadAction<WagesStateValues>) {
      state.values = JSON.parse(JSON.stringify(action.payload));
    },
    updateKey(
      state,
      action: PayloadAction<{
        key:
          | keyof WagesStateValues
          | keyof TForeignEmployerRow
          | keyof TDidYourEmployerProvideAnAllowanceOnTopOfDirectSalaryListRow;
        value: any;
      }>,
    ) {
      lodashSet(state, `values.${action.payload.key}`, action.payload.value);
    },
    updateFieldArrayKey(
      state,
      action: PayloadAction<{
        key:
          | keyof WagesStateValues
          | keyof TForeignEmployerRow
          | keyof TDidYourEmployerProvideAnAllowanceOnTopOfDirectSalaryListRow;
        value: TForeignEmployer;
      }>,
    ) {
      const fieldArray = action.payload.value.map((field) => ({
        ...field,
      }));
      if (action.payload.key === 'foreignEmployer') {
        state.values[action.payload.key] = fieldArray;
      } else {
        lodashSet(state, `values.${action.payload.key}`, fieldArray);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      questionnairesApi.endpoints.getQuestionnaireById.matchFulfilled,
      (state, { payload }) => {
        state.values = payload.questionnaire.data.taxQuestionnaire
          ? payload.questionnaire.data.taxQuestionnaire.spouseIncome.wages
              .values
          : initialState.values;
      },
    );
  },
});

export const { update, updateKey, updateFieldArrayKey } = wagesSlice.actions;
export default wagesSlice.reducer;

export const selectWages = (state: RootState) =>
  state.taxQuestionnaire.spouseIncome.wages;
